import { useEffect, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * Topbar breadcrumbs. Crumbs are derived from the route; detail pages can
 * replace the generic last label ("Organization") with the record name by
 * calling `useCrumbLabel(name)`.
 */

let dynamicLabel: string | null = null;
const listeners = new Set<() => void>();

function setDynamicLabel(label: string | null) {
  if (dynamicLabel === label) return;
  dynamicLabel = label;
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Name the current record in the breadcrumb trail (cleared on unmount). */
export function useCrumbLabel(label: string | null | undefined) {
  useEffect(() => {
    setDynamicLabel(label || null);
    return () => setDynamicLabel(null);
  }, [label]);
}

interface Crumb {
  label: string;
  to?: string;
}

const SECTIONS: Record<string, { label: string; one?: string; parent?: Crumb }> = {
  organizations: { label: 'Organizations', one: 'Organization' },
  products: { label: 'Apps', one: 'App' },
  clients: { label: 'OAuth clients', one: 'Client' },
  users: { label: 'Users', one: 'User' },
  access: { label: 'My access' },
  'access-matrix': { label: 'Access matrix' },
  audit: { label: 'Audit log' },
  keys: { label: 'Signing keys' },
  'mcp-access': { label: 'Hosted MCP' },
  settings: { label: 'Settings' },
  docs: { label: 'API reference', parent: { label: 'Developer', to: '/docs' } },
  playground: { label: 'OAuth playground', parent: { label: 'Developer', to: '/docs' } },
};

export function buildCrumbs(pathname: string, dynamic: string | null): Crumb[] {
  const parts = pathname.split('/').filter(Boolean);
  const root: Crumb = { label: 'Overview', to: '/dashboard' };
  const first = parts[0];
  if (!first || first === 'dashboard') return [{ label: 'Overview' }];
  const section = SECTIONS[first];
  if (!section) return [root, { label: first }];
  const base = section.parent ?? root;
  const crumbs: Crumb[] = [base];
  if (parts.length === 1) return [...crumbs, { label: section.label }];
  if (first === 'playground') return [...crumbs, { label: section.label }];
  crumbs.push({ label: section.label, to: `/${first}` });
  const second = parts[1];
  if (second === 'new') return [...crumbs, { label: `New ${(section.one ?? '').toLowerCase()}`.trim() }];
  const record: Crumb = { label: dynamic || section.one || second };
  if (parts[2] === 'edit') {
    return [...crumbs, { ...record, to: `/${first}/${second}` }, { label: 'Edit' }];
  }
  return [...crumbs, record];
}

export function Breadcrumbs() {
  const { pathname } = useLocation();
  const dynamic = useSyncExternalStore(
    subscribe,
    () => dynamicLabel,
    () => null
  );
  const crumbs = buildCrumbs(pathname, dynamic);
  const last = crumbs.length - 1;
  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1.5 text-[13px] text-muted-foreground">
        {crumbs.map((c, i) => (
          <li key={`${i}-${c.label}`} className={i < last ? 'hidden items-center gap-1.5 sm:flex' : 'flex min-w-0 items-center'}>
            {i < last ? (
              <>
                {c.to ? (
                  <Link to={c.to} className="hover:text-foreground hover:underline">
                    {c.label}
                  </Link>
                ) : (
                  <span>{c.label}</span>
                )}
                <ChevronRight className="h-3 w-3 opacity-50" aria-hidden="true" />
              </>
            ) : (
              <span aria-current="page" className="truncate font-medium text-foreground">
                {c.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
